import {Component, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {PlayerPrefs} from '../classes/PlayerPrefs.js';
import {AudioManager, globalAudioManager} from '@wonderlandengine/spatial-audio';
import {Sounds} from '../enums/Sounds.js';

const MUSIC_ENABLED_KEY = 'MusicEnabled';

const music = 'audio/music/';

export class MusicDirector extends Component {
    static TypeName = 'music-director';

    static MusicFiles: {path: string | string[]; id: Sounds}[] = [
        {path: music + 'Song 1 Slow.mp3', id: Sounds.Song1Slow},
        {path: music + 'Song 3 Slow.mp3', id: Sounds.Song3Slow},
        {path: music + 'Song 4 Slow.mp3', id: Sounds.Song4Slow},
        {path: music + 'Song 5 Slow.mp3', id: Sounds.Song5Slow},
        {path: music + 'Song 1 and 2 Fast.mp3', id: Sounds.Song1And2Fast},
        {path: music + 'ROTB-Title.mp3', id: Sounds.MusicTitle},
    ];

    // Singleton
    private static _instance: MusicDirector;
    private _loadedSongs: Sounds[] = [];
    private _isPaused: boolean;
    static get instance(): MusicDirector {
        return MusicDirector._instance;
    }

    @property.float(0.4)
    musicVolume = 0.4;

    private _currentPlayingId: number = 0;
    private _song: Sounds = Sounds.none;
    private _musicEnabled: boolean = false;

    private get _audioManager(): AudioManager {
        return globalAudioManager as AudioManager;
    }

    get musicEnabled(): boolean {
        return this._musicEnabled;
    }
    set musicEnabled(value: boolean) {
        if (this._musicEnabled !== value) {
            this._musicEnabled = value;
            if (!this._musicEnabled && this._song !== Sounds.none) {
                this.stopImmediate();
            } else if (this._song != Sounds.none) {
                this.play(this._song);
            }

            PlayerPrefs.setBool(MUSIC_ENABLED_KEY, value);
        }
    }

    init() {
        if (MusicDirector._instance) {
            console.error('There can only be one instance of MusicDirector Component');
        }
        MusicDirector._instance = this;

        this._musicEnabled = PlayerPrefs.getBool(MUSIC_ENABLED_KEY, true);
        this._isPaused = false;
    }

    play(song: Sounds) {
        // Check if song is loaded, otherwise load it.
        // It should be cached by the service worker.
        if (!this._loadedSongs.includes(song)) {
            const file = MusicDirector.MusicFiles.find(
                (musicfile) => musicfile.id === song
            );
            if (!file) {
                console.error(`Music file not found for song: ${Sounds[song]}`);
                return;
            }
            this._audioManager.load(file.path, file.id).then(() => {
                this._loadedSongs.push(song);
                // make sure the song requested to play is still the song requested
                if (file.id === song) {
                    this.play(song);
                }
            });
            return;
        }

        if (!navigator.userActivation.hasBeenActive) {
            // Don't play music if the user hasn't interacted with the page yet
            return;
        }
        if (!this._isPaused) {
            this.stopImmediate();
        }
        this._song = song;
        if (!this._musicEnabled) return;
        try {
            this._currentPlayingId = this._audioManager.play(song, {
                channel: 1,
                volume: this.musicVolume,
                loop: true,
                priority: true,
            });
            this._isPaused = false;
        } catch (e) {
            //TK: Converting the error to a warning for now.
            console.warn(e);
        }
    }

    pause() {
        if (this._song === Sounds.none || !this._musicEnabled) {
            // can't pause if nothing was playing and no need to pause if music is disabled
            return;
        }
        this._isPaused = true;
        this._audioManager.pause(this._currentPlayingId);
    }

    resume() {
        if (this._song === Sounds.none || !this._musicEnabled) {
            // can't resume if nothing was playing and no need to resume if music is disabled
            return;
        }
        this._isPaused = false;
        this._audioManager.resume(this._currentPlayingId);
    }

    stopImmediate() {
        if (!this._isPaused) {
            this._audioManager.stop(this._currentPlayingId);
        }
    }
}
